import * as AddBasketOfferEvent from '@ori-events/add-basket-offer';
import * as AddToBasketEvent from '@ori-events/add-to-basket';
import * as AnalyticsEvent from '@ori-events/analytics';
import * as BambuserLiveShoppingEvent from '@ori-events/bambuser-live-shopping';
import * as BannerClickEvent from '@ori-events/banner-click';
import * as BannerDataLoadedEvent from '@ori-events/banner-data-loaded';
import * as ClearBasketEvent from '@ori-events/clear-basket';
import * as DownloadFile from '@ori-events/download-file';
import * as ExpressCheckoutButtonClickEvent from '@ori-events/express-checkout-button-click';
import * as FollowUsItemClickEvent from '@ori-events/follow-us-item-click';
import { HandlePartialViewAnalytics } from '@ori-events/handle-partial-view-analytics';
import * as LinkClickEvent from '@ori-events/link-click';
import * as NewsletterSubscribeEvent from '@ori-events/newsletter-subscribe';
import * as OlapicClickedEvent from '@ori-events/olapic-popup-product-clicked';
import * as PersonalProductListEvent from '@ori-events/personal-product-list';
import * as ProductClickEvent from '@ori-events/product-click';
import * as ProductDataLoadedEvent from '@ori-events/product-data-loaded';
import * as RemoveBasketItemEvent from '@ori-events/remove-basket-item';
import * as SearchSuggestionClickEvent from '@ori-events/search-suggestion-click';
import * as ShareItemClickEvent from '@ori-events/share-item-click';
import * as SmlAnalyticsEvent from '@ori-events/social-media-library-analytics';
import * as ToggleFavoriteEvent from '@ori-events/toggle-favorite';
import * as UpdateBasketItemEvent from '@ori-events/update-basket-item';
import * as UserActionEvent from '@ori-events/user-action';
import * as UserRegisteredEvent from '@ori-events/user-registered';
import * as UserUpgradeRequested from '@ori-events/user-upgrade-requested';
import * as VideoPlayEvent from '@ori-events/video-play';
import * as VirtualPageEvent from '@ori-events/virtual-page';

import {
  forwardEvent as gtmForwardEvent,
  init as initGtm,
  handleAnalyticsEventsFromPartialView as gtmHandleAnalyticsEventsFromPartialView,
} from './gtmAnalytics';
import type { ForwardToChannelMethod, GtmModel } from '~/models';

export const CONFIG_ELEMENT_ID = 'analytics-provider-data';
export const PARTIAL_VIEW_CONFIG_ELEMENT_ID = 'analytics-provider-data-partial';

let isInitialized = false;
const forwardEventMethods: ForwardToChannelMethod[] = [];

// Add needed global analytics event names into this list.
const globalEventNames: string[] = [
  AddBasketOfferEvent.Name,
  AddToBasketEvent.Name,
  BambuserLiveShoppingEvent.Name,
  BannerClickEvent.Name,
  BannerDataLoadedEvent.Name,
  ClearBasketEvent.Name,
  DownloadFile.Name,
  ExpressCheckoutButtonClickEvent.Name,
  FollowUsItemClickEvent.Name,
  LinkClickEvent.Name,
  NewsletterSubscribeEvent.Name,
  OlapicClickedEvent.Name,
  PersonalProductListEvent.Name,
  ProductClickEvent.Name,
  ProductDataLoadedEvent.Name,
  RemoveBasketItemEvent.Name,
  SearchSuggestionClickEvent.Name,
  ShareItemClickEvent.Name,
  SmlAnalyticsEvent.Name,
  ToggleFavoriteEvent.Name,
  UpdateBasketItemEvent.Name,
  UserActionEvent.Name,
  UserRegisteredEvent.Name,
  VideoPlayEvent.Name,
  VirtualPageEvent.Name,
  UserUpgradeRequested.Name,
];

// Adds event listeners for the above mentioned custom events.
// The handler transforms particular CustomEvent to AnalyticsEvent and re-triggers it in global
// context, so that the analytics provider can finally catch it in one unified way.
export function initGlobalAnalyticEvents() {
  globalEventNames.forEach((eventName) =>
    window.addEventListener(eventName, AnalyticsEvent.forwardAnalyticsEvent),
  );
}

function getDataFromElement(configElementId: string) {
  const dataElement = document.getElementById(configElementId);
  if (!dataElement || !dataElement.innerHTML) {
    throw new TypeError("Analytics provider configuration wasn't set properly.");
  }
  const data = dataElement.innerHTML;

  return JSON.parse(data) as GtmModel;
}

// Re-init data from partial view & send basic user identification events.
function handleAnalyticsEventsFromPartialView() {
  const model = getDataFromElement(PARTIAL_VIEW_CONFIG_ELEMENT_ID);
  gtmHandleAnalyticsEventsFromPartialView(model);
}

// Main initialization.
export function init() {
  if (isInitialized) {
    return;
  }

  // So far, only GTM forward channel is included here. Later on, we might add
  // Baidu analytics for China, App Insights, etc. This should be rather dynamic,
  // based on configuration data.
  const model = getDataFromElement(CONFIG_ELEMENT_ID);
  initGtm(model);
  forwardEventMethods.push(gtmForwardEvent);
  isInitialized = true;

  // Connect analytics events (only) here. We suppose that analytics event proxies
  // will do the transformation from original custom events where needed & send
  // the needed analytics events.
  const forwardEventToChannels = (<T>(event: AnalyticsEvent.Event<T>): void =>
    forwardEventMethods.forEach((method) => method(event))) as EventListener;
  window.addEventListener(AnalyticsEvent.Name, forwardEventToChannels);

  // Re-init data from partial view & send basic user identification events.
  window.addEventListener(HandlePartialViewAnalytics, handleAnalyticsEventsFromPartialView);

  // Connect particular event type listeners.
  initGlobalAnalyticEvents();
}

// This helper is used just for testing ATM.
export function resetInitFlag() {
  isInitialized = false;
}
